import type { SALARY_CALCULATION_OPTION } from 'cpool-constants';

import { calcMedian } from '~/utils/calcMedian';
import { calculateAverage } from '~/utils/calculateAverage';

type Props = {
    applicant: 1 | 2;
    salaryMethod: SALARY_CALCULATION_OPTION;
    salaryValues: {
        lastMonthSalaryApplicant1: number | null | undefined;
        lastMonthSalaryApplicant2: number | null | undefined;
        salary1MonthAgoApplicant1: number | null | undefined;
        salary1MonthAgoApplicant2: number | null | undefined;
        salary2MonthAgoApplicant1: number | null | undefined;
        salary2MonthAgoApplicant2: number | null | undefined;
    };
};

export const calculateSalary = ({ applicant, salaryMethod, salaryValues }: Props) => {
    const {
        lastMonthSalaryApplicant1,
        lastMonthSalaryApplicant2,
        salary1MonthAgoApplicant1,
        salary1MonthAgoApplicant2,
        salary2MonthAgoApplicant1,
        salary2MonthAgoApplicant2,
    } = salaryValues;

    const lowestSalary3MonthsApplicant1 = Math.min(
        ...([lastMonthSalaryApplicant1, salary1MonthAgoApplicant1, salary2MonthAgoApplicant1].filter(
            (item) => typeof item === 'number',
        ) as number[]),
    );
    const lowestSalary3MonthsApplicant2 = Math.min(
        ...([lastMonthSalaryApplicant2, salary1MonthAgoApplicant2, salary2MonthAgoApplicant2].filter(
            (item) => typeof item === 'number',
        ) as number[]),
    );
    const lowestSalary2MonthsApplicant1 = Math.min(
        ...([lastMonthSalaryApplicant1, salary1MonthAgoApplicant1].filter(
            (item) => typeof item === 'number',
        ) as number[]),
    );
    const lowestSalary2MonthsApplicant2 = Math.min(
        ...([lastMonthSalaryApplicant2, salary1MonthAgoApplicant2].filter(
            (item) => typeof item === 'number',
        ) as number[]),
    );
    const averageSalary3MonthsApplicant1 = calculateAverage([
        lastMonthSalaryApplicant1,
        salary1MonthAgoApplicant1,
        salary2MonthAgoApplicant1,
    ]);
    const averageSalary3MonthsApplicant2 = calculateAverage([
        lastMonthSalaryApplicant2,
        salary1MonthAgoApplicant2,
        salary2MonthAgoApplicant2,
    ]);
    const averageSalary2MonthsApplicant1 = calculateAverage([lastMonthSalaryApplicant1, salary1MonthAgoApplicant1]);
    const averageSalary2MonthsApplicant2 = calculateAverage([lastMonthSalaryApplicant2, salary1MonthAgoApplicant2]);
    const medianApplicant1 = calcMedian(
        [lastMonthSalaryApplicant1, salary1MonthAgoApplicant1, salary2MonthAgoApplicant1].filter(
            (item) => typeof item === 'number',
        ) as number[],
    );
    const medianApplicant2 = calcMedian(
        [lastMonthSalaryApplicant2, salary1MonthAgoApplicant2, salary2MonthAgoApplicant2].filter(
            (item) => typeof item === 'number',
        ) as number[],
    );
    switch (salaryMethod) {
        case 'LAST_MONTHS_SALARY':
            return applicant === 1 ? lastMonthSalaryApplicant1 : lastMonthSalaryApplicant2;
        case 'LOWEST_SALARY_3_MONTHS':
            return applicant === 1 ? lowestSalary3MonthsApplicant1 : lowestSalary3MonthsApplicant2;
        case 'LOWEST_SALARY_2_MONTHS':
            return applicant === 1 ? lowestSalary2MonthsApplicant1 : lowestSalary2MonthsApplicant2;
        case 'AVERAGE_SALARY_3_MONTHS':
            return applicant === 1 ? averageSalary3MonthsApplicant1 : averageSalary3MonthsApplicant2;
        case 'AVERAGE_SALARY_2_MONTHS':
            return applicant === 1 ? averageSalary2MonthsApplicant1 : averageSalary2MonthsApplicant2;
        case 'MITTELWERT_MEDIAN':
            return applicant === 1 ? medianApplicant1 : medianApplicant2;
        case 'SPECIAL_LOWEST_SALARY_DKB_SKG':
            return applicant === 1
                ? Math.min(medianApplicant1, averageSalary3MonthsApplicant1)
                : Math.min(medianApplicant2, averageSalary3MonthsApplicant2);
        default:
            return applicant === 1 ? lastMonthSalaryApplicant1 : lastMonthSalaryApplicant2;
    }
};
